import React, { useEffect, useRef, useState } from 'react';
import ForestModel from '../components/Deployments/ForestModel';
import DeploymentSelector from '../components/Deployments/DeploymentSelector';
import SnowDepthChart from '../components/Deployments/SnowDephtChart';
import TemperatureChart from '../components/Deployments/TemperatureChart';
import SeasonSelector from '../components/Deployments/SeasonSelector';
import { fetchDeployments, fetchMeteoDataZip } from '../services/api';
import TrajectoryMap from '../components/Deployments/TrajectoryMap';
import DeploymentTrajectoriesList from '../components/Deployments/DeploymentTrajectoriesList';
import { Deployment, TrajectoryInfo } from '../types/types';

interface DeploymentsProps {
  isDark: boolean;
}

export default function Deployments({ isDark }: DeploymentsProps) {
  const forestRef = useRef<HTMLDivElement>(null);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>('2024-11-21');
  const [selectedSeason, setSelectedSeason] = useState<string>('winter');
  const [selectedDeployment, setSelectedDeployment] =
    useState<string>('Deployment_1');
  const [viewMode, setViewMode] = useState<'map' | '3d'>('3d');
  const [selectedInfo, setSelectedInfo] = useState<TrajectoryInfo | null>(null);
  const [showTemperatureChart, setShowTemperatureChart] =
    useState<boolean>(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const toggleChart = (chartType: string) => {
    if (chartType === 'temperature') {
      setShowTemperatureChart(true);
    } else {
      setShowTemperatureChart(false);
    }
  };

  const handleDownload = async () => {
    await fetchMeteoDataZip();
  };

  useEffect(() => {
    fetchDeployments()
      .then((data) => setDeployments(data))
      .catch((err) =>
        console.error('Error while retrieving deployments:', err)
      );
  }, []);

  useEffect(() => {
    if (!forestRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(forestRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleLoad = () => setPageLoaded(true);

    if (document.readyState === 'complete') {
      setPageLoaded(true);
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []);

  return (
    <div className="w-full px-4 sm:px-6 md:px-8 text-gray-800 dark:text-gray-100">
      <div className="mt-6">
        <h2 className="text-xl sm:text-2xl md:text-3xl text-center font-extrabold tracking-tight mb-6">
          Deployment's selection
        </h2>

        <DeploymentSelector
          deploymentDates={deployments}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          setSelectedDeployment={setSelectedDeployment}
        />

        <div className="flex justify-center my-5 space-x-4 items-center">
          <select
            value={showTemperatureChart ? 'temperature' : 'snowDepth'}
            onChange={(e) => toggleChart(e.target.value)}
            className="px-4 py-2 rounded-lg bg-gray-300 text-black font-semibold"
          >
            <option value="temperature">Temperature Chart</option>
            <option value="snowDepth">Snow Depth Chart</option>
          </select>

          <button
            onClick={handleDownload}
            className="w-full sm:w-auto text-sm sm:text-base px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 12l4.5 4.5m0 0l4.5-4.5m-4.5 4.5V3"
              />
            </svg>
            Meteo data
          </button>
        </div>

        <div className="p-4">
          {showTemperatureChart ? (
            <TemperatureChart
              setSelectedDate={setSelectedDate}
              setSelectedDeployment={setSelectedDeployment}
              selectedDate={selectedDate}
              isDark={isDark}
            />
          ) : (
            <SnowDepthChart
              selectedDate={selectedDate}
              setSelectedDeployment={setSelectedDeployment}
              isDark={isDark}
              setSelectedDate={setSelectedDate}
            />
          )}
        </div>
      </div>

      <section className="mt-10">
        <h2 className="text-xl sm:text-2xl md:text-3xl text-center font-extrabold tracking-tight mb-6">
          Selected deployment's trajectory details
        </h2>

        <div className="flex flex-row justify-center items-center space-x-6 mb-6">
          <h3
            className={`text-xl sm:text-2xl md:text-3xl font-bold cursor-pointer ${viewMode === '3d' ? 'text-blue-500' : 'text-gray-700'}`}
            onClick={() => setViewMode('3d')}
          >
            3D Model
          </h3>
          <h3
            className={`text-xl sm:text-2xl md:text-3xl font-bold cursor-pointer ${viewMode === 'map' ? 'text-blue-500' : 'text-gray-700'}`}
            onClick={() => setViewMode('map')}
          >
            Map
          </h3>
        </div>

        {viewMode !== 'map' ? (
          <SeasonSelector
            selectedSeason={selectedSeason}
            setSelectedSeason={setSelectedSeason}
          />
        ) : null}

        <p className="text-sm text-center font-bold cursor-pointer">
          Click on a trajectory to display its information
        </p>

        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
          <div
            className={`w-full ${viewMode === 'map' ? '' : 'md:w-5/6'} min-h-[300px] sm:min-h-[400px] bg-white dark:bg-neutral-900 rounded-lg shadow-lg flex items-center justify-center mt-4 relative`}
          >
            {viewMode === 'map' && pageLoaded ? (
              <TrajectoryMap
                key={selectedDeployment}
                selectedDeployment={selectedDeployment}
              />
            ) : (
              <ForestModel
                selectedDeployment={selectedDeployment}
                selectedInfo={selectedInfo}
                setSelectedInfo={setSelectedInfo}
                selectedSeason={selectedSeason}
              />
            )}
          </div>

          {viewMode !== 'map' ? (
            <div className="w-full md:w-1/6">
              <DeploymentTrajectoriesList
                selectedDeployment={selectedDeployment}
                setSelectedInfo={setSelectedInfo}
              />
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
